import { useMemo } from "react";
import { Page } from "@iq/models";
import { FiAlertCircle, FiArrowRight, FiClipboard, FiGitPullRequest, FiInbox } from "react-icons/fi";
import "./Dashboard.css";

const ACTIVE_INCIDENT_STATUSES = new Set(["new", "triaged", "in_progress"]);
const ACTIVE_PROBLEM_STATUSES = new Set(["new", "investigating", "known_error", "fix_in_progress"]);
const ACTIVE_CHANGE_STATUSES = new Set(["new", "assessment", "approved", "scheduled", "implementing"]);
const URGENT_PRIORITIES = new Set(["critical", "high"]);

function formatStatus(status) {
	return String(status || "new").replace(/_/g, " ");
}

function DashboardStat({ label, value, detail, icon: Icon, onClick }) {
	const content = (
		<>
			<span className="it-dashboard-stat-icon"><Icon aria-hidden="true" /></span>
			<span className="it-dashboard-stat-content">
				<span className="it-dashboard-stat-value">{value}</span>
				<span className="it-dashboard-stat-label">{label}</span>
				<span className="it-dashboard-stat-detail">{detail}</span>
			</span>
		</>
	);

	return onClick ? (
		<button type="button" className="it-dashboard-stat" onClick={onClick}>
			{content}
		</button>
	) : <div className="it-dashboard-stat">{content}</div>;
}

function DashboardQueue({ title, items, emptyMessage, error, renderItem }) {
	return (
		<section className="it-dashboard-queue" aria-label={title}>
			<div className="it-dashboard-queue-heading">
				<h3>{title}</h3>
				<span>{error ? "-" : items.length}</span>
			</div>
			{error ? <p className="it-dashboard-error">{error}</p> : items.length === 0 ? <p className="it-dashboard-empty">{emptyMessage}</p> : (
				<ul className="it-dashboard-queue-list">
					{items.map(renderItem)}
				</ul>
			)}
		</section>
	);
}

function Dashboard({
	incidents = [],
	problems = [],
	changes = [],
	portalRequests = [],
	loading = false,
	incidentsError = "",
	problemsError = "",
	changesError = "",
	portalRequestsError = "",
	onOpenIncidents,
	onOpenPortalRequests,
	onOpenIncident,
	onOpenPortalRequest,
}) {
	const overview = useMemo(() => {
		const incidentItems = Array.isArray(incidents) ? incidents : [];
		const requestItems = Array.isArray(portalRequests) ? portalRequests : [];
		const activeIncidents = incidentItems.filter((incident) => ACTIVE_INCIDENT_STATUSES.has(String(incident?.status || "new")));
		const urgentIncidents = activeIncidents
			.filter((incident) => URGENT_PRIORITIES.has(String(incident?.priority || "").toLowerCase()) || URGENT_PRIORITIES.has(String(incident?.urgency || "").toLowerCase()))
			.sort((left, right) => Number(right?.id || 0) - Number(left?.id || 0));
		const pendingRequests = requestItems
			.filter((request) => ["new", "triaged"].includes(String(request?.status || "new")))
			.sort((left, right) => new Date(right?.created_at || 0) - new Date(left?.created_at || 0));

		return {
			activeIncidents,
			urgentIncidentCount: urgentIncidents.length,
			urgentIncidents: urgentIncidents.slice(0, 5),
			pendingRequests: pendingRequests.slice(0, 5),
			activeProblems: (Array.isArray(problems) ? problems : []).filter((problem) => ACTIVE_PROBLEM_STATUSES.has(String(problem?.status || "new"))).length,
			activeChanges: (Array.isArray(changes) ? changes : []).filter((change) => ACTIVE_CHANGE_STATUSES.has(String(change?.status || "new"))).length,
		};
	}, [incidents, problems, changes, portalRequests]);
	const hasDashboardError = Boolean(incidentsError || problemsError || changesError || portalRequestsError);

	return (
		<Page>
			<div className="it-dashboard">
				<div className="it-dashboard-intro">
					<div>
						<h1>Operations Overview</h1>
						<p>Current work that needs attention across requests, incidents, problems, and changes.</p>
					</div>
					{loading ? <span className="it-dashboard-loading">Refreshing...</span> : null}
				</div>
				{hasDashboardError ? <p className="it-dashboard-error" role="alert">Some dashboard data could not be loaded. Unavailable values are shown as -.</p> : null}

				<div className="it-dashboard-stats">
					<DashboardStat label="Active Incidents" value={incidentsError ? "-" : overview.activeIncidents.length} detail={incidentsError ? "Unavailable" : `${overview.urgentIncidentCount} urgent`} icon={FiAlertCircle} onClick={onOpenIncidents} />
					<DashboardStat label="Portal Requests" value={portalRequestsError ? "-" : overview.pendingRequests.length} detail={portalRequestsError ? "Unavailable" : "Awaiting intake"} icon={FiInbox} onClick={onOpenPortalRequests} />
					<DashboardStat label="Open Problems" value={problemsError ? "-" : overview.activeProblems} detail={problemsError ? "Unavailable" : "Under investigation"} icon={FiClipboard} />
					<DashboardStat label="Active Changes" value={changesError ? "-" : overview.activeChanges} detail={changesError ? "Unavailable" : "In delivery flow"} icon={FiGitPullRequest} />
				</div>

				<div className="it-dashboard-queues">
					<DashboardQueue
						title="Urgent Incidents"
						items={overview.urgentIncidents}
						emptyMessage="No high-priority incidents are active."
						error={incidentsError}
						renderItem={(incident) => (
							<li key={incident?.id}>
								<button type="button" className="it-dashboard-queue-item" onClick={() => onOpenIncident?.(incident)}>
									<span className="it-dashboard-queue-main">
										<strong>{incident?.number || `INC-${incident?.id ?? ""}`}</strong>
										<span>{incident?.title || "Untitled incident"}</span>
									</span>
									<span className={`it-dashboard-status is-${String(incident?.priority || incident?.urgency || "medium").toLowerCase()}`}>{formatStatus(incident?.status)}</span>
									<FiArrowRight aria-hidden="true" />
								</button>
							</li>
						)}
					/>
					<DashboardQueue
						title="Portal Intake"
						items={overview.pendingRequests}
						emptyMessage="No requests are waiting for intake."
						error={portalRequestsError}
						renderItem={(request) => (
							<li key={request?.id}>
								<button type="button" className="it-dashboard-queue-item" onClick={() => onOpenPortalRequest?.(request)}>
									<span className="it-dashboard-queue-main">
										<strong>REQ-{request?.id ?? ""}</strong>
										<span>{request?.subject || "Untitled request"}</span>
									</span>
									<span className="it-dashboard-status">{formatStatus(request?.status)}</span>
									<FiArrowRight aria-hidden="true" />
								</button>
							</li>
						)}
					/>
				</div>
			</div>
		</Page>
	);
}

export default Dashboard;
